import { parseAgentVariables, omitAgentUiConfigVariables } from "../../utils/agentVariables";

export const normalizeAgentName = (name) => {
  if (!name) return "";
  return name
    .toLowerCase()
    .trim()
    .replace(/[-_\s]+/g, " ")
    .replace(/\s+/g, "-")
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const AP_AGENT_SLUG = "ap-agent";

export const isApAgent = (agent) => {
  if (!agent?.name) return false;
  return normalizeAgentName(agent.name) === AP_AGENT_SLUG;
};

/** Skills agent table & playground (non-AP agents) use /api/v1/automation_outputs. */
export const usesAutomationOutputsApi = (agent) => !isApAgent(agent);

export const idsMatch = (a, b) =>
  a != null && b != null && String(a) === String(b);

export const resolveAgentIdFromAutomation = (automation) => {
  if (!automation) return null;

  const candidates = [
    automation.diy_agent_id,
    automation.diy_agent?.id,
    automation.agent_id,
    automation.agent?.id,
  ];

  for (const id of candidates) {
    if (id != null && id !== "") return id;
  }

  const agentIds = automation.agent_ids;
  if (agentIds && typeof agentIds === "object" && !Array.isArray(agentIds)) {
    const first = Object.values(agentIds).find((v) => v != null && v !== "");
    if (first != null) return first;
  }

  return null;
};

/** agent_id for /api/v1/agent/execute_manual_agent (from automation API response). */
export const resolveAutomationAgentId = (automation) => {
  if (!automation) return null;

  const candidates = [
    automation.automation_agent_id,
    automation.automation_agent?.id,
  ];

  for (const id of candidates) {
    if (id != null && id !== "") return id;
  }

  return null;
};

export const serializeAutomationJsonField = (value, fallback = {}) => {
  if (value == null || value === "") return JSON.stringify(fallback);
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return JSON.stringify(fallback);
    try {
      JSON.parse(trimmed);
      if (trimmed.length > 65536) return JSON.stringify(fallback);
      return trimmed;
    } catch {
      return JSON.stringify(fallback);
    }
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const json = JSON.stringify(value);
    if (json.length > 65536) return JSON.stringify(fallback);
    return json;
  }
  return JSON.stringify(fallback);
};

/** Only flat primitive keys for manual-run form fields. */
export const flattenPrimitiveFields = (record) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return {};
  }

  const flat = {};
  for (const [key, value] of Object.entries(record)) {
    if (!key || value == null) continue;
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      flat[key] = typeof value === "string" ? value : String(value);
    }
  }
  return flat;
};

/** @deprecated alias */
export const flattenContextForVariables = flattenPrimitiveFields;

export const parseVariablesObject = (raw) => {
  if (raw == null || raw === "") return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return {};

  const trimmed = raw.trim();
  if (!trimmed) return {};

  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

export const resolveAutomationContextObject = (automation) => {
  if (!automation) return {};

  const raw = automation.context ?? automation.context_json;
  return flattenPrimitiveFields(parseVariablesObject(raw));
};

export const resolveAgentVariablesObject = (agent) => {
  if (!agent) return {};

  const raw =
    agent.fullData?.variables ??
    agent.variables ??
    agent.fullData?.variables_json;

  let flat = {};

  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    flat = flattenPrimitiveFields(raw);
  } else {
    const parsed = parseVariablesObject(raw);
    if (Object.keys(parsed).length > 0) {
      flat = flattenPrimitiveFields(parsed);
    } else {
      flat = flattenPrimitiveFields(parseAgentVariables(raw));
    }
  }

  return omitAgentUiConfigVariables(flat);
};

export const MANUAL_RUN_FILTERS = "{}";
export const MANUAL_RUN_METADATA = JSON.stringify({ exception_status: "running" });

export const resolveAgentQuery = (agent) => {
  if (!agent) return "Extract the details from file";

  const query =
    agent.fullData?.agent_goal ||
    agent.description ||
    agent.fullData?.description ||
    "";

  const trimmed = typeof query === "string" ? query.trim() : "";
  return trimmed || "Extract the details from file";
};

export const resolveAutomationQuery = (automation) => {
  if (!automation) return "fetch invoice details";

  const instructions = automation.instructions;
  if (typeof instructions === "string" && instructions.trim()) {
    return instructions.trim();
  }
  if (instructions && typeof instructions === "object" && !Array.isArray(instructions)) {
    if (typeof instructions.query === "string" && instructions.query.trim()) {
      return instructions.query.trim();
    }
    const first = Object.values(instructions).find(
      (v) => typeof v === "string" && v.trim()
    );
    if (first) return first.trim();
  }

  if (automation.workflow_name?.trim()) {
    return automation.workflow_name.trim();
  }

  return "fetch invoice details";
};

export const kvPairsToObject = (pairs) => {
  if (!Array.isArray(pairs)) return {};
  const result = {};
  for (const pair of pairs) {
    const key = pair?.key?.trim();
    if (!key) continue;
    result[key] = pair.value ?? "";
  }
  return result;
};

export const buildManualAgentStreamOptions = (
  automation,
  { outputTableName, source = "manual run" } = {}
) => {
  if (!automation) {
    return {
      variables: "{}",
      filters: MANUAL_RUN_FILTERS,
      metadata: MANUAL_RUN_METADATA,
      outputTableName: outputTableName || "automation_outputs",
      automationId: null,
      source,
    };
  }

  return {
    variables: serializeAutomationJsonField(resolveAutomationContextObject(automation)),
    filters: MANUAL_RUN_FILTERS,
    metadata: MANUAL_RUN_METADATA,
    outputTableName: outputTableName || "automation_outputs",
    automationId: automation.id ?? null,
    source,
  };
};

/** Automation module (/automation) always uses /api/v1/automations. */
